import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import axios from 'axios';
import OrderStatusBadge from './components/OrderStatusBadge';
import PaymentCardDisplay from './components/PaymentCardDisplay';
import OrderDetailSkeleton from './skeletons/OrderDetailSkeleton';

const Container = styled.div`
  padding: 24px 16px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: #f3f4f6;
  color: #479de6;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 6px;
  margin-bottom: 24px;
  text-decoration: none;
  border: 1px solid #e5e7eb;

  &:hover {
    background-color: #e5e7eb;
    color: #3a8ed4;
    text-decoration: none;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

const OrderIdHeader = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OrderDate = styled.div`
  font-size: 16px;
  color: #666;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CardHeader = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #479de6;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #666;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: ${(props) => (props.bold ? '600' : '400')};
`;

const StudentSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StudentName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const EventName = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
`;

const ItemCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  background: #e5e7eb;
`;

const ItemPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 4px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const ItemMeta = styled.div`
  font-size: 13px;
  color: #666;
`;

const ItemPrice = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-top: 4px;
`;

const BackgroundsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const BackgroundThumb = styled.img`
  width: 40px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
`;

const BackgroundItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackgroundLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const PoseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const PoseThumb = styled.img`
  width: 40px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
`;

const PoseLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const ChildItems = styled.div`
  margin-left: 20px;
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
`;

const ChildItemRow = styled.div`
  padding: 8px 0;
  font-size: 13px;
  color: #666;
  display: flex;
  justify-content: space-between;
`;

const ChildItemCard = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const ChildItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
`;

const ChildPoseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const ChildBackgroundRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TotalsCard = styled(Card)`
  margin-top: 24px;
`;

const TotalsRow = styled(InfoRow)`
  ${(props) =>
    props.isTotal &&
    `
    border-top: 2px solid #eee;
    margin-top: 8px;
    padding-top: 16px;
  `}
`;

const TotalLabel = styled(InfoLabel)`
  ${(props) =>
    props.isTotal &&
    `
    font-size: 16px;
    font-weight: 600;
    color: #333;
  `}
`;

const TotalValue = styled(InfoValue)`
  ${(props) =>
    props.isTotal &&
    `
    font-size: 18px;
  `}

  ${(props) =>
    props.isRefund &&
    `
    color: #ea580c;
  `}
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const formatPrice = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const OrderDetailPage = ({ match }) => {
  const { orderId } = match.params;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`/api/v2/parent/orders/${orderId}`);
        setOrder(response.data.order);
      } catch (err) {
        console.error('Failed to fetch order:', err);
        if (err.response?.status === 404) {
          setError('Order not found');
        } else {
          setError('Failed to load order details. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="bnl-dashboard-container">
        <div className="bnl-dashboard">
          <Container>
            <BackLink to="/my-orders">&larr; Back to Orders</BackLink>
            <OrderDetailSkeleton />
          </Container>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bnl-dashboard-container">
        <div className="bnl-dashboard">
          <Container>
            <BackLink to="/my-orders">&larr; Back to Orders</BackLink>
            {error === 'Order not found' ? (
              <NotFoundMessage>
                <h2>Order Not Found</h2>
                <p>
                  The order you're looking for doesn't exist or you don't have
                  access to it.
                </p>
              </NotFoundMessage>
            ) : (
              <ErrorMessage>{error}</ErrorMessage>
            )}
          </Container>
        </div>
      </div>
    );
  }

  const { totals, payment, sub_orders } = order;

  return (
    <div className="bnl-dashboard-container">
      <div className="bnl-dashboard">
        <Container>
          <BackLink to="/my-orders">&larr; Back to Orders</BackLink>

          <HeaderSection>
            <OrderIdHeader>
              {order.code}
              <OrderStatusBadge
                isCancelled={order.is_cancelled}
                totalRefunded={order.total_refunded}
                grandTotal={totals.grand_total}
              />
            </OrderIdHeader>
            <OrderDate>{order.formatted_date}</OrderDate>
          </HeaderSection>

          {/* Order Summary Card */}
          <Card>
            <CardHeader>Order Summary</CardHeader>
            <InfoRow>
              <InfoLabel>Payment Method</InfoLabel>
              <InfoValue>
                <PaymentCardDisplay
                  cardBrand={payment.card_brand}
                  cardLast4={payment.card_last4}
                />
              </InfoValue>
            </InfoRow>
          </Card>

          {/* Items by Student */}
          {sub_orders.map((subOrder) => (
            <Card key={subOrder.id}>
              <StudentSection>
                <StudentName>
                  {subOrder.student.first_name} {subOrder.student.last_name}
                </StudentName>
                {subOrder.event?.name && (
                  <EventName>
                    {subOrder.event.name}
                    {subOrder.event.date && ` - ${subOrder.event.date}`}
                  </EventName>
                )}

                {subOrder.items.map((item) => (
                  <ItemCard key={item.id}>
                    {item.image_thumbnail ? (
                      <ItemImage src={item.image_thumbnail} alt={item.name} />
                    ) : (
                      <ItemPlaceholder>No Image</ItemPlaceholder>
                    )}
                    <ItemDetails>
                      <ItemName>{item.name}</ItemName>
                      <ItemMeta>Qty: {item.quantity}</ItemMeta>
                      {item.is_bonus ? (
                        <ItemPrice>FREE</ItemPrice>
                      ) : (
                        <ItemPrice>{formatPrice(item.price)}</ItemPrice>
                      )}

                      {/* Product Options */}
                      {item.product_options &&
                        item.product_options.length > 0 && (
                          <ItemMeta style={{ marginTop: '4px' }}>
                            Options:{' '}
                            {item.product_options
                              .map((opt) => opt.name)
                              .join(', ')}
                          </ItemMeta>
                        )}

                      {/* Selected Pose */}
                      {item.selected_pose && (
                        <PoseRow
                          onContextMenu={(event) => event.preventDefault()}
                        >
                          {item.selected_pose.image_url && (
                            <PoseThumb
                              src={item.selected_pose.image_url}
                              alt={item.selected_pose.name || 'Selected pose'}
                            />
                          )}
                          <PoseLabel>
                            Pose: {item.selected_pose.name || 'Selected'}
                          </PoseLabel>
                        </PoseRow>
                      )}

                      {/* Backgrounds - hide for YPX products (they show per child item) */}
                      {item.backgrounds &&
                        item.backgrounds.length > 0 &&
                        (!item.child_items ||
                          item.child_items.length === 0) && (
                          <BackgroundsRow>
                            {item.backgrounds.map((bg, idx) => (
                              <BackgroundItem key={idx}>
                                {bg.thumbnail_url && (
                                  <BackgroundThumb
                                    src={bg.thumbnail_url}
                                    alt={bg.name || 'Background'}
                                  />
                                )}
                                <BackgroundLabel>
                                  {bg.name}
                                  {bg.price > 0 &&
                                    ` (+${formatPrice(bg.price)})`}
                                </BackgroundLabel>
                              </BackgroundItem>
                            ))}
                          </BackgroundsRow>
                        )}

                      {/* Child Items (for bundles/packages) */}
                      {item.child_items && item.child_items.length > 0 && (
                        <ChildItems>
                          {item.child_items.map((child) => (
                            <ChildItemCard key={child.id}>
                              <ChildItemName>
                                {child.name} (x{child.quantity})
                              </ChildItemName>

                              {/* Child's selected pose */}
                              {child.selected_pose && (
                                <ChildPoseRow
                                  onContextMenu={(event) =>
                                    event.preventDefault()
                                  }
                                >
                                  {child.selected_pose.image_url && (
                                    <PoseThumb
                                      src={child.selected_pose.image_url}
                                      alt={child.selected_pose.name}
                                    />
                                  )}
                                  <PoseLabel>
                                    Pose: {child.selected_pose.name}
                                  </PoseLabel>
                                </ChildPoseRow>
                              )}

                              {/* Child's selected background */}
                              {child.backgrounds &&
                                child.backgrounds.length > 0 && (
                                  <ChildBackgroundRow>
                                    {child.backgrounds.map((bg, idx) => (
                                      <BackgroundItem key={idx}>
                                        {bg.thumbnail_url && (
                                          <BackgroundThumb
                                            src={bg.thumbnail_url}
                                            alt={bg.name}
                                          />
                                        )}
                                        <BackgroundLabel>
                                          {bg.name}
                                          {bg.price > 0 &&
                                            ` (+${formatPrice(bg.price)})`}
                                        </BackgroundLabel>
                                      </BackgroundItem>
                                    ))}
                                  </ChildBackgroundRow>
                                )}
                            </ChildItemCard>
                          ))}
                        </ChildItems>
                      )}
                    </ItemDetails>
                  </ItemCard>
                ))}
              </StudentSection>
            </Card>
          ))}

          {/* Order Totals */}
          <TotalsCard>
            <CardHeader>Order Total</CardHeader>

            <TotalsRow>
              <TotalLabel>Subtotal</TotalLabel>
              <TotalValue>{formatPrice(totals.subtotal)}</TotalValue>
            </TotalsRow>

            {totals.discount > 0 && (
              <TotalsRow>
                <TotalLabel>Discount</TotalLabel>
                <TotalValue>-{formatPrice(totals.discount)}</TotalValue>
              </TotalsRow>
            )}

            {totals.tax > 0 && (
              <TotalsRow>
                <TotalLabel>Tax</TotalLabel>
                <TotalValue>{formatPrice(totals.tax)}</TotalValue>
              </TotalsRow>
            )}

            {totals.shipping > 0 && (
              <TotalsRow>
                <TotalLabel>Shipping</TotalLabel>
                <TotalValue>{formatPrice(totals.shipping)}</TotalValue>
              </TotalsRow>
            )}

            {totals.service_fee > 0 && (
              <TotalsRow>
                <TotalLabel>Service Fee</TotalLabel>
                <TotalValue>{formatPrice(totals.service_fee)}</TotalValue>
              </TotalsRow>
            )}

            <TotalsRow isTotal>
              <TotalLabel isTotal>Total</TotalLabel>
              <TotalValue isTotal bold>
                {formatPrice(totals.grand_total)}
              </TotalValue>
            </TotalsRow>

            {order.total_refunded > 0 && (
              <TotalsRow>
                <TotalLabel>Refunded</TotalLabel>
                <TotalValue isRefund>
                  -{formatPrice(order.total_refunded)}
                </TotalValue>
              </TotalsRow>
            )}
          </TotalsCard>
        </Container>
      </div>
    </div>
  );
};

OrderDetailPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      orderId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default OrderDetailPage;
